/**
 * @file twoFactorApi.ts
 * @brief Two-step verification (B31, ADR-059): API calls and queries for the
 *        Security tab, the Super Admin staff switch and reset, and the
 *        company "require for all members" setting.
 *
 * @details
 * The sign-in side (verify, setup with a setup token) lives in AuthContext;
 * this file is everything that runs with a normal session.
 *
 *   useTwoFactorStatus      GET    /api/auth/2fa/status
 *   startSetup / confirm    POST   /api/auth/2fa/setup/start, /confirm
 *   disable                 POST   /api/auth/2fa/disable { code | backupCode }
 *   regenerateBackupCodes   POST   /api/auth/2fa/backup-codes { code }
 *   forgetTrustedDevices    DELETE /api/auth/2fa/trusted-devices
 *   useStaffTwoFactor       GET/PUT /api/admin/security/two-factor (PUT: Owner)
 *   resetUserTwoFactor      POST   /api/admin/users/:id/2fa/reset
 *   useCompanySecurity      GET/PUT /api/company/security
 *
 * Backup codes are returned to the caller and never cached: the queries here
 * hold only counts and flags. £0 running cost (TOTP, no SMS).
 *
 * @references
 * - docs/product/two-step-verification.md
 *
 * @author DES
 * @date 25-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ApiClientError, apiDelete, apiGet, apiPost, apiPut } from "@/shared/api/client";
import { adminEndpoints, authEndpoints, companyEndpoints } from "@/shared/api/endpoints";
import type { TwoFactorSetupInfo } from "@/shared/auth/auth-context";
import { meExtrasKey } from "@/shared/auth/meExtras";
import {
  challengeFromAnswer,
  forgetTrustedDevice,
  twoFactorErrorText,
  type TwoFactorChallenge,
} from "./twoFactorRules";

/** The 2FA challenge in a failed sign-in, or null. */
export function challengeFromError(err: unknown): TwoFactorChallenge | null {
  if (!(err instanceof ApiClientError)) return null;
  return challengeFromAnswer(err.status, err.code, errorBody(err));
}

/** The JSON body of a failed call (for `attemptsLeft`, `retryAfterSeconds`). */
export function errorBody(err: unknown): unknown {
  if (!(err instanceof ApiClientError)) return null;
  const raw = err.raw as { response?: { data?: unknown } } | undefined;
  return raw?.response?.data ?? null;
}

/** The wording for a failed second-step call. */
export function describeTwoFactorError(err: unknown): string {
  if (err instanceof ApiClientError) return twoFactorErrorText(err.code, err.message, errorBody(err));
  if (err instanceof Error && err.message) return err.message;
  return "We couldn't check the code. Please try again.";
}

export const SetupInfoSchema = z
  .object({
    otpauthUri: z.string(),
    qrCodeDataUrl: z.string().startsWith("data:image/"),
    setupKey: z.string(),
    issuer: z.string().optional(),
    accountName: z.string().optional(),
    digits: z.number().optional(),
    period: z.number().optional(),
  })
  .passthrough();

export function parseSetupInfo(raw: unknown): TwoFactorSetupInfo {
  return SetupInfoSchema.parse(raw);
}

export const BackupCodesSchema = z.object({ backupCodes: z.array(z.string()).min(1) }).passthrough();

const StatusSchema = z
  .object({
    enabled: z.boolean(),
    enabledAt: z.string().nullable().optional(),
    required: z.boolean().optional().default(false),
    requiredReason: z.enum(["owner", "superadmin", "company"]).nullable().optional().catch(null),
    backupCodesLeft: z.number().nullable().optional(),
    trustedDevices: z.number().optional().default(0),
    trustDeviceAllowed: z.boolean().optional().default(true),
    isOwner: z.boolean().optional().default(false),
    /** False when the server has no encryption key: setup is refused (503). */
    available: z.boolean().optional().default(true),
  })
  .passthrough();
export type TwoFactorStatus = z.infer<typeof StatusSchema>;

export const twoFactorStatusKey = ["auth", "2fa", "status"] as const;

/** GET /2fa/status; null when this backend has no 2FA yet (404). */
export function useTwoFactorStatus() {
  return useQuery<TwoFactorStatus | null>({
    queryKey: twoFactorStatusKey,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return StatusSchema.parse(await apiGet<unknown>(authEndpoints.twoFactorStatus));
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

/** Profile → Security shows once the server answers the status route. */
export function useTwoFactorTabVisible(): boolean {
  return Boolean(useTwoFactorStatus().data);
}

function useRefreshTwoFactor() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: twoFactorStatusKey });
    void qc.invalidateQueries({ queryKey: meExtrasKey });
    // A Super Admin's own change shows in the staff list beside it.
    void qc.invalidateQueries({ queryKey: staffKey });
  };
}

export function useStartSetup() {
  return useMutation({
    mutationFn: async () => parseSetupInfo(await apiPost<unknown>(authEndpoints.twoFactorSetupStart, {})),
  });
}

export function useConfirmSetup() {
  const refresh = useRefreshTwoFactor();
  return useMutation({
    mutationFn: async (code: string) =>
      BackupCodesSchema.parse(await apiPost<unknown>(authEndpoints.twoFactorSetupConfirm, { code })).backupCodes,
    onSuccess: refresh,
  });
}

export function useDisableTwoFactor() {
  const refresh = useRefreshTwoFactor();
  return useMutation({
    mutationFn: (proof: { code?: string; backupCode?: string }) =>
      apiPost<unknown>(authEndpoints.twoFactorDisable, proof),
    onSuccess: () => {
      // Off means the code is never asked, so a trust token here is moot.
      forgetTrustedDevice();
      refresh();
    },
  });
}

export function useRegenerateBackupCodes() {
  const refresh = useRefreshTwoFactor();
  return useMutation({
    mutationFn: async (code: string) =>
      BackupCodesSchema.parse(await apiPost<unknown>(authEndpoints.twoFactorBackupCodes, { code })).backupCodes,
    onSuccess: refresh,
  });
}

export function useForgetTrustedDevices() {
  const refresh = useRefreshTwoFactor();
  return useMutation({
    mutationFn: async () => {
      const r = await apiDelete<{ revoked?: number }>(authEndpoints.twoFactorTrustedDevices);
      return typeof r?.revoked === "number" ? r.revoked : 0;
    },
    onSuccess: () => {
      forgetTrustedDevice();
      refresh();
    },
  });
}

// ----------------------------------------------------------------------
// Super Admin: staff enforcement (Owner only to change) + reset
// ----------------------------------------------------------------------

const StaffSchema = z
  .object({
    staffRequired: z.boolean(),
    available: z.boolean().optional().default(true),
    ownerSet: z.boolean().optional().default(false),
    staff: z
      .array(
        z
          .object({
            id: z.string(),
            email: z.string(),
            name: z.string().nullable().optional(),
            isOwner: z.boolean().optional().default(false),
            twoFactorEnabled: z.boolean().optional().default(false),
          })
          .passthrough(),
      )
      .default([]),
  })
  .passthrough();
export type StaffTwoFactor = z.infer<typeof StaffSchema>;

const staffKey = ["admin", "security", "two-factor"] as const;

export function useStaffTwoFactor(enabled: boolean) {
  return useQuery<StaffTwoFactor | null>({
    queryKey: staffKey,
    enabled,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return StaffSchema.parse(await apiGet<unknown>(adminEndpoints.staffTwoFactor));
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

export function useSaveStaffTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (staffRequired: boolean) =>
      StaffSchema.parse(await apiPut<unknown>(adminEndpoints.staffTwoFactor, { staffRequired })),
    onSuccess: (data) => {
      qc.setQueryData(staffKey, data);
      // The switch makes the Owner's own 2FA required (no "Turn off").
      void qc.invalidateQueries({ queryKey: twoFactorStatusKey });
    },
  });
}

export function useResetUserTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiPost<{ reset?: boolean; wasOn?: boolean }>(adminEndpoints.userTwoFactorReset(userId), {}),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
      void qc.invalidateQueries({ queryKey: staffKey });
    },
  });
}

// ----------------------------------------------------------------------
// Company admin: require for all members
// ----------------------------------------------------------------------

const CompanySecuritySchema = z
  .object({
    require2fa: z.boolean(),
    members: z.number().optional().default(0),
    membersWith2fa: z.number().optional().default(0),
    available: z.boolean().optional().default(true),
  })
  .passthrough();
export type CompanySecurity = z.infer<typeof CompanySecuritySchema>;

const companySecurityKey = ["company", "security"] as const;

export function useCompanySecurity(enabled = true) {
  return useQuery<CompanySecurity | null>({
    queryKey: companySecurityKey,
    enabled,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return CompanySecuritySchema.parse(await apiGet<unknown>(companyEndpoints.security));
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

export function useSaveCompanySecurity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (require2fa: boolean) =>
      CompanySecuritySchema.parse(await apiPut<unknown>(companyEndpoints.security, { require2fa })),
    onSuccess: (data) => qc.setQueryData(companySecurityKey, data),
  });
}
